import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} [cartDiscountErrorConnection] - Shown when the request itself fails.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #pending = false;

  #disableControls() {
    const previousBusy = this.getAttribute('aria-busy');
    const input = this.querySelector('input[name="discount"]');
    const previousReadOnly = input instanceof HTMLInputElement && input.readOnly;
    const buttons = Array.from(this.querySelectorAll('button'), (button) => ({
      button,
      previousDisabled: button.getAttribute('aria-disabled'),
    }));

    this.setAttribute('aria-busy', 'true');
    if (input instanceof HTMLInputElement) input.readOnly = true;
    for (const { button } of buttons) button.setAttribute('aria-disabled', 'true');

    return () => {
      if (previousBusy === null) this.removeAttribute('aria-busy');
      else this.setAttribute('aria-busy', previousBusy);

      if (input instanceof HTMLInputElement) input.readOnly = previousReadOnly;
      for (const { button, previousDisabled } of buttons) {
        if (previousDisabled === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', previousDisabled);
      }
    };
  }

  /** Keep controls guarded while waiting for another basket writer and rendering. */
  async #runMutation(work) {
    if (this.#pending) return;
    this.#pending = true;
    const restoreControls = this.#disableControls();
    try {
      // This theme loads cart-items as a script, without an import-map alias.
      // Use its registered class so both controls share the same queue instance.
      if (!customElements.get('cart-items-component')) {
        await customElements.whenDefined('cart-items-component');
      }
      const CartItems = customElements.get('cart-items-component');
      return await CartItems.runDiscountMutation(async () => {
        if (this.isConnected === false) return;
        return work();
      });
    } finally {
      this.#pending = false;
      restoreControls();
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const requestedInput = event.target instanceof HTMLFormElement
      ? event.target.querySelector('input[name="discount"]') : null;
    const requestedCode = requestedInput instanceof HTMLInputElement ? requestedInput.value.trim() : undefined;
    return this.#runMutation(async () => {
      const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;


      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const discountCode = form.querySelector('input[name="discount"]');
      if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

      if (typeof requestedCode !== 'string') return;
      const discountCodeValue = requestedCode;
      if (!discountCodeValue) {
        cartDiscountErrorShipping.classList.add('hidden');
        this.#handleDiscountError('discount_code');
        return;
      }

      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      const abortController = this.#createAbortController();

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');
      this.refs.cartDiscountErrorConnection?.classList.add('hidden');

      const allDiscountCodes = [...existingDiscounts, discountCodeValue];
      const deferredPromise = CartDiscountUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: allDiscountCodes.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({
            discount: allDiscountCodes.join(','),
            sections: [this.dataset.sectionId],
          }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        const { data, newHtml, section } = await this.#readResponse(response, abortController);

        if (
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === false;
          })
        ) {
          discountCode.value = '';
          this.#handleDiscountError('discount_code');
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }

        const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
        if (section) {
          const codes = Array.from(discountCodes)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          // Before morphing, we need to check if the shipping discount is applicable in the UI
          // we check the liquid logic compared to the cart payload to assess whether we leveraged
          // a valid shipping discount code.
          if (
            codes.length === existingDiscounts.length &&
            codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
            data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })
          ) {
            this.#handleDiscountError('shipping');
            discountCode.value = '';
            deferredPromise.resolve({
              cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
            });
            return;
          }
        }

        await morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
        // morphSection no longer syncs the input value from the server-rendered empty state,
        // so without this the user's typed code stays in the field after a successful apply.
        discountCode.value = '';
      } catch (error) {
        deferredPromise.reject(error);
        if (!abortController.signal.aborted && error instanceof Error && error.name !== 'AbortError') {
          // A dropped connection or an error page from Shopify used to leave the
          // customer with no message at all.
          cartDiscountErrorShipping.classList.add('hidden');
          this.#handleDiscountError('connection');
          this.dispatchEvent(
            new CartErrorEvent({
              error: error.message || 'Failed to apply discount',
              code: 'SERVICE_UNAVAILABLE',
            })
          );
        }
      } finally {
        if (this.#activeFetch === abortController) this.#activeFetch = null;
        cartPerformance.measureFromEvent('discount-update:user-action', event);
      }
    });
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    return this.#runMutation(async () => {

      if (
        (event instanceof KeyboardEvent && event.key !== 'Enter') ||
        !(event instanceof MouseEvent) ||
        !(event.target instanceof HTMLElement) ||
        typeof this.dataset.sectionId !== 'string'
      ) {
        return;
      }

      const pill = event.target.closest('.cart-discount__pill');
      if (!(pill instanceof HTMLLIElement)) return;

      const discountCode = pill.dataset.discountCode;
      if (!discountCode) return;

      const existingDiscounts = this.#existingDiscounts();
      const index = existingDiscounts.indexOf(discountCode);
      if (index === -1) return;

      existingDiscounts.splice(index, 1);
      this.refs.cartDiscountError.classList.add('hidden');
      this.refs.cartDiscountErrorDiscountCode.classList.add('hidden');
      this.refs.cartDiscountErrorShipping.classList.add('hidden');
      this.refs.cartDiscountErrorConnection?.classList.add('hidden');

      const abortController = this.#createAbortController();
      const deferredPromise = CartDiscountUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: existingDiscounts.map((code) => ({ code })),
          promise: deferredPromise.promise,
        })
      );

      try {
        const config = fetchConfig('json', {
          body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
        });

        const response = await fetch(Theme.routes.cart_update_url, {
          ...config,
          signal: abortController.signal,
        });

        const { data, newHtml } = await this.#readResponse(response, abortController);

        await morphSection(this.dataset.sectionId, newHtml, {
          mode: this.closest('theme-drawer') ? 'hydration' : 'full',
        });
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
      } catch (error) {
        deferredPromise.reject(error);
        if (!abortController.signal.aborted && error instanceof Error && error.name !== 'AbortError') {
          this.#handleDiscountError('connection');
          this.dispatchEvent(
            new CartErrorEvent({
              error: error.message || 'Failed to remove discount',
              code: 'SERVICE_UNAVAILABLE',
            })
          );
        }
      } finally {
        if (this.#activeFetch === abortController) this.#activeFetch = null;
      }
    });
  };

  async #readResponse(response, abortController) {
    const data = await response.json();
    if (abortController.signal.aborted) throw new DOMException('Superseded discount update', 'AbortError');
    if (!response.ok || !Array.isArray(data?.items) || !Array.isArray(data?.discount_codes)) {
      throw new Error('Unable to confirm the discount update');
    }
    const newHtml = data.sections?.[this.dataset.sectionId];
    if (typeof newHtml !== 'string' || !newHtml.trim()) {
      throw new Error('The updated basket could not be displayed');
    }
    const section = new DOMParser().parseFromString(newHtml, 'text/html')
      .getElementById(`shopify-section-${this.dataset.sectionId}`);
    if (!section) throw new Error('The updated basket section is missing');
    return { data, newHtml, section };
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'connection'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountErrorConnection } =
      this.refs;
    const targets = {
      discount_code: cartDiscountErrorDiscountCode,
      shipping: cartDiscountErrorShipping,
      // The connection message is optional markup; fall back to the code message.
      connection: cartDiscountErrorConnection ?? cartDiscountErrorDiscountCode,
    };
    cartDiscountError.classList.remove('hidden');
    for (const element of Object.values(targets)) element?.classList.add('hidden');
    targets[type].classList.remove('hidden');

    const errorMessage =
      type === 'discount_code'
        ? 'Invalid discount code'
        : type === 'shipping'
          ? 'Discount not applicable for shipping'
          : 'Discount request failed';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
